import { useAuth } from '../../context/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import posVendaService from '../../services/posVenda/PosVendaService';
import {
  ConfirmacaoEnderecoConclusaoRequest,
  PosVendaConfirmacaoEnderecoFilaQuery,
  SalvarEnderecoConfirmadoRequest,
} from '../../types/posVenda';
import { invalidatePosVendaQueries } from './usePosVenda';
import { POS_VENDA_STALE_MS, posVendaQueryKeys } from './posVendaQueryKeys';

// O identificador opaco separa sessões sem colocar credenciais nas query keys.
let ultimaSessao: { userId: string | null; token: string | null; scope: string } | undefined;
let sequenciaSessao = 0;
export function confirmacaoEnderecoSessionScope(userId: string | null, token: string | null): string {
  if (!ultimaSessao || ultimaSessao.userId !== userId || ultimaSessao.token !== token) {
    ultimaSessao = { userId, token, scope: `sessao-${++sequenciaSessao}` };
  }
  return ultimaSessao.scope;
}
export function useConfirmacaoSessionScope() {
  const { user, token } = useAuth();
  return confirmacaoEnderecoSessionScope(user?.id ?? null, token);
}

export function useConfirmacaoEnderecoFilaQuery(
  query: PosVendaConfirmacaoEnderecoFilaQuery = {},
  enabled = true
) {
  const session = useConfirmacaoSessionScope();
  return useQuery({
    queryKey: posVendaQueryKeys.confirmacaoEnderecoFila(query, session),
    queryFn: ({ signal }) => posVendaService.listarConfirmacaoEnderecoFila(query, { signal }),
    staleTime: POS_VENDA_STALE_MS,
    enabled,
    retry: 1,
  });
}

export function useConfirmacaoEnderecoMetricasQuery(enabled = true) {
  const session = useConfirmacaoSessionScope();
  return useQuery({
    queryKey: posVendaQueryKeys.confirmacaoEnderecoMetricas(session),
    queryFn: ({ signal }) => posVendaService.getConfirmacaoEnderecoMetricas({ signal }),
    staleTime: POS_VENDA_STALE_MS,
    enabled,
    retry: 1,
  });
}

export function useConfirmacaoEnderecoPorContratoQuery(contractId: string | null, enabled = true) {
  const session = useConfirmacaoSessionScope();
  return useQuery({
    queryKey: posVendaQueryKeys.confirmacaoEnderecoContrato(contractId ?? '', session),
    queryFn: ({ signal }) => posVendaService.getConfirmacaoEnderecoPorContrato(contractId!, { signal }),
    staleTime: POS_VENDA_STALE_MS,
    enabled: enabled && !!contractId,
    retry: 1,
  });
}

export function useSalvarEnderecoConfirmadoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ filaId, dto }: { filaId: string; dto: SalvarEnderecoConfirmadoRequest }) =>
      posVendaService.salvarEnderecoConfirmado(filaId, dto),
    onSuccess: () => {
      void invalidatePosVendaQueries(qc);
    },
  });
}

export function useConcluirConfirmacaoEnderecoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      protocoloId,
      dto,
    }: {
      protocoloId: string;
      dto: ConfirmacaoEnderecoConclusaoRequest & { concurrencyToken?: string | number };
    }) => posVendaService.concluirConfirmacaoEndereco(protocoloId, dto),
    onSuccess: () => {
      void invalidatePosVendaQueries(qc);
    },
  });
}
